/* eslint-disable */

import map from 'lodash/map';
import filter from 'lodash/filter';
import axios, {
  AxiosResponse,
  HttpStatusCode,
  AxiosRequestConfig,
} from 'axios';
import config from 'src/config';
import { getAccessToken, removeAccessToken } from './authentication';

const { endpointHost } = config;
const noBodyCodes = [204, 205, 401, 404, 500];

axios.defaults.baseURL = endpointHost;
axios.defaults.headers.common.Accept = 'application/vnd.falcon.v3+json';
axios.defaults.headers.common['X-Requested-With'] = 'XMLHttpRequest';
axios.defaults.headers.common['Content-Type'] = 'application/json';
axios.defaults.headers.common['Accept-Language'] = 'en';

axios.interceptors.response.use(
  (response) => response, // Return response if no error
  (error) => {
    // Check if it's a 401 error
    if (error.response && error.response.status === 401) {
      // Remove access token and log the user out
      removeAccessToken();
      window.location.href = '/login';
    }

    // If it's not a 401 error, reject the promise with the error
    return Promise.reject(error);
  },
);

export const hasNoBody = (response: { status: HttpStatusCode }) => {
  return noBodyCodes.includes(response.status);
};

export const setupAxios = (accessToken?: string) => {
  const token = accessToken ?? getAccessToken();

  if (token != null)
    axios.defaults.headers.common.Authorization = `Bearer ${token}`;
  else if (token === null) delete axios.defaults.headers.common.Authorization;
  axios.defaults.headers.common['Content-Language'] = 'en';
};

const http = () => axios;

export const getRequest = async <T = any>(
  url: string,
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<T>> => {
  return await axios.get<T>(url, options);
};

export const postRequest = async <T = any>(
  url: string,
  data?: any,
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<T>> => {
  return await axios.post<T>(url, data, options);
};

export const deleteRequest = async <T = any>(
  url: string,
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<T>> => {
  return await axios.delete<T>(url, options);
};

export const putRequest = async <T = any>(
  url: string,
  data?: any,
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<T>> => {
  return await axios.put(url, data, options);
};

export const patchRequest = async <T = any>(
  url: string,
  data?: any,
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<T>> => {
  return await axios.patch(url, data, options);
};

export const paginationMapper = (meta: UCM.MetaRawType): UCM.MetaType => ({
  totalData: meta.total_data,
  totalPages: meta.total_pages,
  currentPage: meta.current_page,
});

/**
 * Convert a JSON object, returning a query string
 */
export function serialize(params: Record<string, any>, prefix = ''): string {
  if (!params) return '';

  const query = Object.keys(params).reduce<string[]>((accum, item) => {
    let key = item;
    const value = params[key];

    if (Array.isArray(params)) {
      key = `${prefix}[]`;
    } else if (typeof params === 'object') {
      key = prefix ? `${prefix}[${key}]` : key;
    }

    if (value === undefined) {
      return accum;
    }

    if (typeof value === 'object' && value !== null) {
      const moreParams = serialize(value, key);
      if (moreParams) accum.push(moreParams);
    } else {
      accum.push(`${key}=${encodeURIComponent(value)}`);
    }

    return accum;
  }, []);

  return query.join('&');
}

export const resolveQueryString = <
  T extends Record<string, any>,
  U extends Record<string, any>,
>(
  filters: Array<U>,
  params?: T,
): string => {
  const queryString = [
    params && serialize(params),
    ...map(filters, (filterParams) => serialize(filterParams, 'filters')),
  ];
  const query = filter(queryString, Boolean).join('&');
  return query ? `?${query}` : '';
};

export default http;

export const resolveClearFilters = (
  filters: Record<string, string | boolean | number | string[]>,
) => {
  if (!filters || typeof filters !== 'object') {
    return {};
  }
  return Object.fromEntries(
    Object.entries(filters).filter(([_, value]) => {
      if (typeof value === 'string') {
        return value?.trim() !== '';
      }

      return value;
    }),
  );
};
